// AuthClient — auth-api SDK.
// 두 레포 (portal, admin) 가 동일 파일 복제 사용. Phase 5 에서 사내 패키지 분리.

import type {
  AdminOrgItem,
  AssignSiteCctvRoleInput,
  ApiError,
  ApiKeyCreated,
  ApiKeyItem,
  AuditLogItem,
  AuditStatsResponse,
  DashboardStats,
  DelegationDetailedItem,
  DescendantOrgItem,
  ExternalCredentialItem,
  JoinRequest,
  MfaChallengeResponse,
  MeResponse,
  MemberItem,
  OrganizationDetail,
  OrganizationItem,
  OrganizationSearchItem,
  PermissionItem,
  RelationshipItem,
  ServiceContractDetail,
  ServiceContractItem,
  ServiceItem,
  SiteItem,
  SiteCctvRoleItem,
  SiteCctvRolesResponse,
  TokenResponse,
  UserPublic,
  UserSiteMembership,
  UpdateSiteCctvRoleInput,
} from './types';

export interface AuthClientConfig {
  baseUrl: string;
}

export class AuthApiError extends Error {
  status: number;
  code: string;
  detail: ApiError;

  constructor(status: number, detail: ApiError) {
    super(detail.code);
    this.status = status;
    this.code = detail.code;
    this.detail = detail;
  }
}

const ACCESS_KEY = 'ontoh.auth.access';
const REFRESH_KEY = 'ontoh.auth.refresh';
const ORG_KEY = 'ontoh.auth.current_org_id';

export class AuthClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshing: Promise<void> | null = null;

  constructor(config: AuthClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.accessToken = localStorage.getItem(ACCESS_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_KEY);
  }

  // ─── 토큰 ────────────────────────────────────────

  isAuthenticated(): boolean {
    return Boolean(this.accessToken);
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  getCurrentOrgId(): string | null {
    return localStorage.getItem(ORG_KEY);
  }

  setCurrentOrgId(orgId: string | null): void {
    if (orgId) localStorage.setItem(ORG_KEY, orgId);
    else localStorage.removeItem(ORG_KEY);
  }

  private setTokens(access: string, refresh: string): void {
    this.accessToken = access;
    this.refreshToken = refresh;
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ORG_KEY);
  }

  // ─── HTTP ────────────────────────────────────────

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    skipRefresh = false,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.accessToken)
      headers['Authorization'] = `Bearer ${this.accessToken}`;

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !skipRefresh && this.refreshToken) {
      try {
        await this.attemptRefresh();
        return this.request<T>(method, path, body, true);
      } catch {
        this.clearTokens();
        throw new AuthApiError(401, { code: 'session_expired' });
      }
    }

    if (res.status === 204) return undefined as T;

    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      const detail =
        (data.detail as ApiError | undefined) ?? (data as ApiError);
      throw new AuthApiError(res.status, detail);
    }
    return data as T;
  }

  private async attemptRefresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      const res = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
      if (!res.ok) {
        throw new Error('refresh_failed');
      }
      const data = (await res.json()) as TokenResponse;
      this.setTokens(data.access_token, data.refresh_token);
    })();
    try {
      await this.refreshing;
    } finally {
      this.refreshing = null;
    }
  }

  // ─── 인증 ────────────────────────────────────────

  async login(
    identifier: string,
    password: string,
  ): Promise<TokenResponse | MfaChallengeResponse> {
    const data = await this.request<
      TokenResponse | MfaChallengeResponse
    >('POST', '/v1/auth/login', { identifier, password }, true);
    if ('mfa_required' in data && data.mfa_required) {
      return data;
    }
    const tokens = data as TokenResponse;
    this.setTokens(tokens.access_token, tokens.refresh_token);
    return tokens;
  }

  /** Phase 5.5b — login challenge 응답 후 OTP/backup 으로 토큰 발급. */
  async loginMfa(challengeId: string, code: string): Promise<TokenResponse> {
    const data = await this.request<TokenResponse>(
      'POST',
      '/v1/auth/login/mfa',
      { challenge_id: challengeId, code },
      true,
    );
    this.setTokens(data.access_token, data.refresh_token);
    return data;
  }

  // ─── MFA TOTP (Phase 5.5) ────────────────────────

  // Phase 5c.C — method/recipient 분기. body 없으면 기본 'totp' (기존 호출자 호환).
  async mfaSetup(
    body: { method?: string; recipient?: string } = {},
  ): Promise<{
    method: string;
    secret: string | null;
    otpauth_uri: string | null;
    recipient_hint: string | null;
  }> {
    return this.request<{
      method: string;
      secret: string | null;
      otpauth_uri: string | null;
      recipient_hint: string | null;
    }>('POST', '/v1/auth/mfa/setup', body);
  }

  async mfaEnable(code: string): Promise<{ backup_codes: string[] }> {
    return this.request<{ backup_codes: string[] }>(
      'POST',
      '/v1/auth/mfa/enable',
      { code },
    );
  }

  async mfaVerify(code: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      '/v1/auth/mfa/verify',
      { code },
    );
  }

  async mfaRegenerateBackupCodes(code: string): Promise<{ backup_codes: string[] }> {
    return this.request<{ backup_codes: string[] }>(
      'POST',
      '/v1/auth/mfa/backup-codes',
      { code },
    );
  }

  async mfaDisable(): Promise<void> {
    await this.request<void>('DELETE', '/v1/auth/mfa');
  }

  async signup(input: {
    identifier: { email?: string; person_id?: string };
    password: string;
    name: string;
    phone?: string;
  }): Promise<{ user: { id: string }; access_token: string; refresh_token: string }> {
    const data = await this.request<{
      user: { id: string };
      access_token: string;
      refresh_token: string;
      expires_in: number;
    }>('POST', '/v1/users/signup', input, true);
    this.setTokens(data.access_token, data.refresh_token);
    return data;
  }

  async logout(): Promise<void> {
    try {
      await this.request<void>('POST', '/v1/auth/logout', {
        refresh_token: this.refreshToken,
      });
    } catch {
      // 무시 — 클라이언트는 어쨌든 로컬 토큰 삭제
    }
    this.clearTokens();
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      '/v1/auth/forgot-password',
      { email },
      true,
    );
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      '/v1/auth/reset-password',
      { token, new_password: newPassword },
      true,
    );
  }

  async getMe(): Promise<MeResponse> {
    return this.request<MeResponse>('GET', '/v1/auth/me');
  }

  async updateMe(data: { name?: string; phone?: string }): Promise<MeResponse> {
    return this.request<MeResponse>('PUT', '/v1/auth/me', data);
  }

  async changePassword(current: string, next: string): Promise<void> {
    await this.request<void>('PUT', '/v1/auth/me/password', {
      current_password: current,
      new_password: next,
    });
  }

  // ─── 무소속 재가입 ────────────────────────────────

  async searchOrganizations(
    q: string,
    type?: string,
    limit = 20,
  ): Promise<OrganizationSearchItem[]> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (type) params.set('org_type', type);
    const data = await this.request<{ items: OrganizationSearchItem[] }>(
      'GET',
      `/v1/organizations/search?${params}`,
    );
    return data.items;
  }

  async requestJoin(
    organizationId: string,
    message?: string,
    requestedRole = 'org_member',
  ): Promise<JoinRequest> {
    return this.request<JoinRequest>('POST', '/v1/organization-join-requests', {
      organization_id: organizationId,
      requested_role: requestedRole,
      message,
    });
  }

  async myJoinRequests(): Promise<JoinRequest[]> {
    const data = await this.request<{ items: JoinRequest[] }>(
      'GET',
      '/v1/organization-join-requests?user_id=me',
    );
    return data.items;
  }

  async cancelJoinRequest(id: string): Promise<JoinRequest> {
    return this.request<JoinRequest>(
      'POST',
      `/v1/organization-join-requests/${id}/cancel`,
    );
  }

  // ─── Organizations (admin) ────────────────────────

  async listOrganizations(params: {
    org_type?: string;
    parent_org_id?: string;
    q?: string;
    limit?: number;
  } = {}): Promise<OrganizationItem[]> {
    const sp = new URLSearchParams();
    if (params.org_type) sp.set('org_type', params.org_type);
    if (params.parent_org_id) sp.set('parent_org_id', params.parent_org_id);
    if (params.q) sp.set('q', params.q);
    if (params.limit) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    const data = await this.request<{ items: OrganizationItem[] }>(
      'GET',
      `/v1/organizations${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async getOrganization(id: string): Promise<OrganizationDetail> {
    return this.request<OrganizationDetail>('GET', `/v1/organizations/${id}`);
  }

  async createOrganization(input: {
    name: string;
    org_type: string;
    business_number?: string | null;
    address?: string | null;
    parent_org_id?: string;
    relation_type?: string;
    metadata?: Record<string, unknown>;
  }): Promise<OrganizationItem & { relationship: RelationshipItem | null }> {
    return this.request('POST', '/v1/organizations', input);
  }

  async updateOrganization(
    id: string,
    data: {
      name?: string;
      business_number?: string | null;
      address?: string | null;
      metadata?: Record<string, unknown>;
    },
  ): Promise<OrganizationItem> {
    return this.request('PUT', `/v1/organizations/${id}`, data);
  }

  async deleteOrganization(id: string, confirmName: string): Promise<{
    status: string;
    cascade_summary: Record<string, number>;
  }> {
    return this.request(
      'DELETE',
      `/v1/organizations/${id}?confirm_name=${encodeURIComponent(confirmName)}`,
    );
  }

  async listOrgMembers(orgId: string, status: string = 'active'): Promise<MemberItem[]> {
    const data = await this.request<{ items: MemberItem[] }>(
      'GET',
      `/v1/organizations/${orgId}/members?status=${status}`,
    );
    return data.items;
  }

  async addMember(
    orgId: string,
    input: { user_id: string; role: string; is_primary?: boolean },
  ) {
    return this.request('POST', `/v1/organizations/${orgId}/members`, input);
  }

  async patchMember(
    orgId: string,
    userId: string,
    data: { role?: string; is_primary?: boolean },
  ) {
    return this.request('PATCH', `/v1/organizations/${orgId}/members/${userId}`, data);
  }

  async removeMember(orgId: string, userId: string): Promise<void> {
    await this.request<void>(
      'DELETE',
      `/v1/organizations/${orgId}/members/${userId}`,
    );
  }

  async listRelationships(
    orgId: string,
    direction: 'parents' | 'children' | 'both' = 'both',
  ): Promise<{ parents: RelationshipItem[]; children: RelationshipItem[] }> {
    return this.request(
      'GET',
      `/v1/organizations/${orgId}/relationships?direction=${direction}`,
    );
  }

  async createRelationship(
    orgId: string,
    input: { parent_org_id: string; relation_type?: string; ends_at?: string | null; notes?: string },
  ): Promise<RelationshipItem> {
    return this.request('POST', `/v1/organizations/${orgId}/relationships`, input);
  }

  async endRelationship(orgId: string, relId: string): Promise<void> {
    await this.request<void>(
      'DELETE',
      `/v1/organizations/${orgId}/relationships/${relId}`,
    );
  }

  // ─── Sites ───────────────────────────────────────

  async listSites(params: {
    owning_org_id?: string;
    status?: 'active' | 'archived' | 'deleted';
    q?: string;
  } = {}): Promise<SiteItem[]> {
    const sp = new URLSearchParams();
    if (params.owning_org_id) sp.set('owning_org_id', params.owning_org_id);
    if (params.status) sp.set('status', params.status);
    if (params.q) sp.set('q', params.q);
    const qs = sp.toString();
    const data = await this.request<{ items: SiteItem[] }>(
      'GET',
      `/v1/sites${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async getSite(id: string): Promise<SiteItem> {
    return this.request<SiteItem>('GET', `/v1/sites/${id}`);
  }

  async createSite(input: {
    name: string;
    owning_org_id: string;
    address?: string | null;
    starts_at?: string;
    metadata?: Record<string, unknown>;
  }): Promise<SiteItem> {
    return this.request('POST', '/v1/sites', input);
  }

  async updateSite(
    id: string,
    data: { name?: string; address?: string | null; metadata?: Record<string, unknown> },
  ): Promise<SiteItem> {
    return this.request('PUT', `/v1/sites/${id}`, data);
  }

  async archiveSite(id: string, ends_at?: string): Promise<SiteItem> {
    return this.request('POST', `/v1/sites/${id}/archive`, { ends_at });
  }

  async reactivateSite(id: string): Promise<SiteItem> {
    return this.request('POST', `/v1/sites/${id}/reactivate`);
  }

  // 조직 멤버 역할을 수정하지 않는 현장별 CCTV 서비스 역할.
  async listSiteCctvRoles(siteId: string): Promise<SiteCctvRolesResponse> {
    return this.request('GET', `/v1/sites/${encodeURIComponent(siteId)}/cctv-roles`);
  }

  async assignSiteCctvRole(
    siteId: string,
    input: AssignSiteCctvRoleInput,
  ): Promise<SiteCctvRoleItem> {
    return this.request('POST', `/v1/sites/${encodeURIComponent(siteId)}/cctv-roles`, input);
  }

  async updateSiteCctvRole(
    siteId: string,
    userId: string,
    input: UpdateSiteCctvRoleInput,
  ): Promise<SiteCctvRoleItem> {
    return this.request('PATCH', `/v1/sites/${encodeURIComponent(siteId)}/cctv-roles/${encodeURIComponent(userId)}`, input);
  }

  async revokeSiteCctvRole(siteId: string, userId: string, expectedVersion: number): Promise<void> {
    await this.request<void>(
      'DELETE',
      `/v1/sites/${encodeURIComponent(siteId)}/cctv-roles/${encodeURIComponent(userId)}?expected_version=${expectedVersion}`,
    );
  }

  // ─── User Site Memberships (승인 대기함) ───────────

  async listUsmBySite(siteId: string, status?: string): Promise<UserSiteMembership[]> {
    const sp = new URLSearchParams({ site_id: siteId });
    if (status) sp.set('status', status);
    const data = await this.request<{ items: UserSiteMembership[] }>(
      'GET',
      `/v1/user-site-memberships?${sp}`,
    );
    return data.items;
  }

  async approveUsm(id: string): Promise<UserSiteMembership> {
    return this.request('POST', `/v1/user-site-memberships/${id}/approve`);
  }

  async rejectUsm(id: string, reason: string): Promise<UserSiteMembership> {
    return this.request('POST', `/v1/user-site-memberships/${id}/reject`, {
      rejection_reason: reason,
    });
  }

  // ─── Org Join Requests (승인 대기함) ───────────────

  async listJoinRequestsForOrg(orgId: string, status?: string): Promise<JoinRequest[]> {
    const sp = new URLSearchParams({ organization_id: orgId });
    if (status) sp.set('status', status);
    const data = await this.request<{ items: JoinRequest[] }>(
      'GET',
      `/v1/organization-join-requests?${sp}`,
    );
    return data.items;
  }

  async approveJoinRequest(id: string, override_role?: string) {
    return this.request<{ join_request: JoinRequest; membership_created: unknown }>(
      'POST',
      `/v1/organization-join-requests/${id}/approve`,
      override_role ? { override_role } : {},
    );
  }

  async rejectJoinRequest(id: string, reason: string) {
    return this.request<JoinRequest>(
      'POST',
      `/v1/organization-join-requests/${id}/reject`,
      { rejection_reason: reason },
    );
  }

  // ─── Services / Contracts ─────────────────────────

  async listServices(): Promise<ServiceItem[]> {
    const data = await this.request<{ items: ServiceItem[] }>('GET', '/v1/services');
    return data.items;
  }

  async listContracts(params: {
    org_id?: string;
    relationship_id?: string; // legacy/deprecated Phase 7c-7
    site_id?: string;
    service_id?: string;
    status?: string;
  } = {}): Promise<ServiceContractItem[]> {
    const sp = new URLSearchParams();
    if (params.org_id) sp.set('org_id', params.org_id);
    if (params.relationship_id) sp.set('relationship_id', params.relationship_id);
    if (params.site_id) sp.set('site_id', params.site_id);
    if (params.service_id) sp.set('service_id', params.service_id);
    if (params.status) sp.set('status', params.status);
    const qs = sp.toString();
    const data = await this.request<{ items: ServiceContractItem[] }>(
      'GET',
      `/v1/service-contracts${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  /** Phase 7c-7 — org-scoped (relationship_id 제거). 본인 조직만 계약 보유. */
  async createContract(input: {
    org_id: string;
    service_id: string;
    site_id?: string | null;
    enabled?: boolean;
    starts_at?: string;
    ends_at?: string;
    notes?: string;
  }): Promise<ServiceContractItem> {
    return this.request('POST', '/v1/service-contracts', input);
  }

  /** Phase 7c-7 — 낙관적 잠금. version 가 일치해야 성공. */
  async revokeContract(id: string, version?: number): Promise<void> {
    const qs = version !== undefined ? `?version=${version}` : '';
    await this.request<void>('DELETE', `/v1/service-contracts/${id}${qs}`);
  }

  /** Phase 7c-2 / 7c-7 — 계약 enabled/ends_at/notes 변경. 낙관적 잠금 (version 필수). */
  async updateContract(
    id: string,
    data: {
      version: number;
      enabled?: boolean;
      ends_at?: string | null;
      notes?: string | null;
    },
  ): Promise<ServiceContractItem> {
    return this.request('PUT', `/v1/service-contracts/${id}`, data);
  }

  /** Phase 7c-5 — 계약 상세 (nested service / relationship.parent_org/child_org / site). */
  async getContract(id: string): Promise<ServiceContractDetail> {
    return this.request<ServiceContractDetail>('GET', `/v1/service-contracts/${id}`);
  }

  /** Phase 7c-5 — 조직의 모든 후손 (BFS, depth/parent 정보 포함). */
  async getDescendants(
    orgId: string,
    params: { include_self?: boolean; max_depth?: number } = {},
  ): Promise<DescendantOrgItem[]> {
    const sp = new URLSearchParams();
    if (params.include_self !== undefined) sp.set('include_self', String(params.include_self));
    if (params.max_depth !== undefined) sp.set('max_depth', String(params.max_depth));
    const qs = sp.toString();
    const data = await this.request<{ items: DescendantOrgItem[] }>(
      'GET',
      `/v1/organizations/${orgId}/descendants${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  /** Phase 7c-7 — 조직의 모든 조상 (BFS, depth=1 직상위, depth=2 상상위 ...). */
  async getAncestors(
    orgId: string,
    params: { include_self?: boolean; max_depth?: number } = {},
  ): Promise<DescendantOrgItem[]> {
    const sp = new URLSearchParams();
    if (params.include_self !== undefined) sp.set('include_self', String(params.include_self));
    if (params.max_depth !== undefined) sp.set('max_depth', String(params.max_depth));
    const qs = sp.toString();
    const data = await this.request<{ items: DescendantOrgItem[] }>(
      'GET',
      `/v1/organizations/${orgId}/ancestors${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  // ─── Permissions / Delegations ────────────────────

  async listPermissions(): Promise<PermissionItem[]> {
    const data = await this.request<{ items: PermissionItem[] }>('GET', '/v1/permissions');
    return data.items;
  }

  async listDelegations(params: {
    relationship_id?: string;
    from_org_id?: string;
    to_org_id?: string;
    permission_code?: string;
    status?: string;
  } = {}): Promise<DelegationDetailedItem[]> {
    const sp = new URLSearchParams();
    if (params.relationship_id) sp.set('relationship_id', params.relationship_id);
    if (params.from_org_id) sp.set('from_org_id', params.from_org_id);
    if (params.to_org_id) sp.set('to_org_id', params.to_org_id);
    if (params.permission_code) sp.set('permission_code', params.permission_code);
    if (params.status) sp.set('status', params.status);
    const qs = sp.toString();
    const data = await this.request<{ items: DelegationDetailedItem[] }>(
      'GET',
      `/v1/delegations${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async createDelegation(input: {
    relationship_id: string;
    permission_code: string;
    starts_at?: string;
    ends_at?: string;
    notes?: string;
  }) {
    return this.request('POST', '/v1/delegations', input);
  }

  async revokeDelegation(id: string): Promise<void> {
    await this.request<void>('DELETE', `/v1/delegations/${id}`);
  }

  // ─── Users (admin) ─────────────────────────────────

  async listUsers(params: {
    organization_id?: string;
    search?: string;
    status?: string;
    limit?: number;
  } = {}): Promise<UserPublic[]> {
    const sp = new URLSearchParams();
    if (params.organization_id) sp.set('organization_id', params.organization_id);
    if (params.search) sp.set('search', params.search);
    if (params.status) sp.set('status', params.status);
    if (params.limit) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    const data = await this.request<{ items: UserPublic[] }>(
      'GET',
      `/v1/users${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async inviteUser(input: {
    email: string;
    organization_id: string;
    org_role: 'org_admin' | 'org_member';
    message?: string;
  }) {
    return this.request<{
      invitation: unknown | null;
      join_request: unknown | null;
      mode: string | null;
      email_sent: boolean;
    }>('POST', '/v1/users/invite', input);
  }

  async activateUser(id: string): Promise<UserPublic> {
    return this.request('POST', `/v1/users/${id}/activate`);
  }

  async deactivateUser(id: string): Promise<UserPublic> {
    return this.request('POST', `/v1/users/${id}/deactivate`);
  }

  async unlockUser(id: string): Promise<UserPublic> {
    return this.request('POST', `/v1/users/${id}/unlock`);
  }

  // ─── M2M ───────────────────────────────────────────

  async listApiKeys(params: { organization_id?: string; type?: string; status?: string } = {}): Promise<ApiKeyItem[]> {
    const sp = new URLSearchParams();
    if (params.organization_id) sp.set('organization_id', params.organization_id);
    if (params.type) sp.set('type', params.type);
    if (params.status) sp.set('status', params.status);
    const qs = sp.toString();
    const data = await this.request<{ items: ApiKeyItem[] }>(
      'GET',
      `/v1/api-keys${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async createApiKey(input: {
    organization_id: string;
    name: string;
    type: 'edge' | 'integration' | 'generic' | 'service';
    scopes: string[];
    expires_at?: string;
  }): Promise<ApiKeyCreated> {
    return this.request('POST', '/v1/api-keys', input);
  }

  async rotateApiKey(id: string, gracePeriodSeconds = 3600): Promise<ApiKeyCreated> {
    return this.request('POST', `/v1/api-keys/${id}/rotate`, {
      grace_period_seconds: gracePeriodSeconds,
    });
  }

  async revokeApiKey(id: string): Promise<void> {
    await this.request<void>('DELETE', `/v1/api-keys/${id}`);
  }

  async listExternalCredentials(params: { organization_id?: string } = {}): Promise<ExternalCredentialItem[]> {
    const sp = new URLSearchParams();
    if (params.organization_id) sp.set('organization_id', params.organization_id);
    const qs = sp.toString();
    const data = await this.request<{ items: ExternalCredentialItem[] }>(
      'GET',
      `/v1/external-credentials${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async createExternalCredential(input: {
    organization_id: string;
    system_code: string;
    name: string;
    credential_data: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    expires_at?: string;
  }): Promise<ExternalCredentialItem> {
    return this.request('POST', '/v1/external-credentials', input);
  }

  async revokeExternalCredential(id: string): Promise<void> {
    await this.request<void>('DELETE', `/v1/external-credentials/${id}`);
  }

  // ─── Admin (ops) ──────────────────────────────────

  async getDashboard(): Promise<DashboardStats> {
    return this.request<DashboardStats>('GET', '/v1/admin/dashboard');
  }

  // Phase 5c.B — audit_logs 활동 집계
  async getAuditStats(params: {
    window_hours?: number;
    bucket_minutes?: number;
  } = {}): Promise<AuditStatsResponse> {
    const q = new URLSearchParams();
    if (params.window_hours) q.set('window_hours', String(params.window_hours));
    if (params.bucket_minutes) q.set('bucket_minutes', String(params.bucket_minutes));
    const qs = q.toString();
    return this.request<AuditStatsResponse>(
      'GET',
      `/v1/admin/audit-logs/stats${qs ? '?' + qs : ''}`,
    );
  }

  async listAdminOrganizations(params: {
    q?: string;
    org_type?: string;
    status?: string;
  } = {}): Promise<AdminOrgItem[]> {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.org_type) sp.set('org_type', params.org_type);
    if (params.status) sp.set('status', params.status);
    const qs = sp.toString();
    const data = await this.request<{ items: AdminOrgItem[] }>(
      'GET',
      `/v1/admin/organizations${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async listAuditLogs(params: {
    actor_user_id?: string;
    action?: string;
    actions?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  } = {}): Promise<AuditLogItem[]> {
    const sp = new URLSearchParams();
    if (params.actor_user_id) sp.set('actor_user_id', params.actor_user_id);
    if (params.action) sp.set('action', params.action);
    if (params.actions) sp.set('action__in', params.actions);
    if (params.start_date) sp.set('start_date', params.start_date);
    if (params.end_date) sp.set('end_date', params.end_date);
    if (params.limit) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    const data = await this.request<{ items: AuditLogItem[] }>(
      'GET',
      `/v1/admin/audit-logs${qs ? `?${qs}` : ''}`,
    );
    return data.items;
  }

  async emergencyLockUser(id: string, reason: string) {
    return this.request('POST', `/v1/admin/users/${id}/emergency-lock`, { reason });
  }

  async emergencyUnlockUser(id: string) {
    return this.request('POST', `/v1/admin/users/${id}/emergency-unlock`);
  }

  async forceLogoutUser(id: string) {
    return this.request('POST', `/v1/admin/users/${id}/force-logout`);
  }

  async forcePasswordResetUser(id: string) {
    return this.request<{ ok: boolean; detail: { reset_token?: string } }>(
      'POST',
      `/v1/admin/users/${id}/force-password-reset`,
    );
  }
}

let _instance: AuthClient | null = null;

export function getAuthClient(): AuthClient {
  if (_instance) return _instance;
  const baseUrl =
    (import.meta as ImportMeta & { env: { VITE_AUTH_API_URL?: string } }).env
      .VITE_AUTH_API_URL ?? 'http://localhost:8001';
  _instance = new AuthClient({ baseUrl });
  return _instance;
}
